import { usePollingManager, POLLING_CADENCES } from '@calendly/polling'

/**
 * Polling config that adapts to SSE connection: when SSE is connected we poll less
 * frequently; when not, we use more aggressive intervals. Shared by meetings list and
 * meeting detail so features/meetings can reuse the same pattern.
 *
 * All configs use usePollingManager's default skipPollingIfUnfocused: true, so polling
 * runs only when the tab is focused (RTK Query + setupListeners).
 */

/** When true, use relaxed (slower) polling; when false, use aggressive (faster) polling. */
export interface UseAdaptivePollingConfigParams {
  /** True when realtime SSE is connected so we can poll less often. */
  isSSEConnected: boolean
  /**
   * 'list' = configs for list view (active + background + fetchOnce).
   * 'detail' = single config for selected meeting detail polling.
   */
  variant: 'list' | 'detail'
  /**
   * When false, all returned configs have pollingInterval: 0 (disabled).
   * Use to gate polling behind a feature flag. Defaults to true.
   */
  enabled?: boolean
}

export interface UseAdaptivePollingConfigListResult {
  /** For visible-range / active queries. When SSE: 30s (PERIODIC), else 10s (STANDARD). */
  activePollingConfig: ReturnType<typeof usePollingManager>
  /** For non-visible queries. 60s (BACKGROUND) in both cases. */
  backgroundPollingConfig: ReturnType<typeof usePollingManager>
  /** No polling (fetch once). Use when a query should only run initially. */
  fetchOnceNoPollingConfig: ReturnType<typeof usePollingManager>
}

export interface UseAdaptivePollingConfigDetailResult {
  /** For selected meeting detail. When SSE: no polling (rely on invalidations); else 5s (FAST). */
  detailPollingConfig: ReturnType<typeof usePollingManager>
}

/**
 * Returns polling configs that depend on whether SSE is connected. Use in meetings list
 * (variant 'list') or meeting detail (variant 'detail') to poll less when SSE is connected.
 */
export function useAdaptivePollingConfig(
  params: UseAdaptivePollingConfigParams & { variant: 'list' }
): UseAdaptivePollingConfigListResult
export function useAdaptivePollingConfig(
  params: UseAdaptivePollingConfigParams & { variant: 'detail' }
): UseAdaptivePollingConfigDetailResult
export function useAdaptivePollingConfig(
  params: UseAdaptivePollingConfigParams
): UseAdaptivePollingConfigListResult | UseAdaptivePollingConfigDetailResult {
  const { isSSEConnected, variant, enabled = true } = params

  // Active/visible list: relaxed when SSE (60s), aggressive when not (10s)
  const activeWhenSSE = usePollingManager({ cadence: POLLING_CADENCES.BACKGROUND, enabled })
  const activeWhenNoSSE = usePollingManager({ cadence: POLLING_CADENCES.STANDARD, enabled })
  const backgroundPollingConfig = usePollingManager({
    cadence: POLLING_CADENCES.BACKGROUND,
    enabled,
    refetchOnReconnect: false,
  })
  const fetchOnceNoPollingConfig = usePollingManager({
    customInterval: 0,
    enabled,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  // Detail: no polling when SSE (rely on event invalidations); aggressive when not (5s)
  const detailWhenNoSSE = usePollingManager({ cadence: POLLING_CADENCES.FAST, enabled, refetchOnFocus: false })

  if (variant === 'list') {
    const activePollingConfig = isSSEConnected ? activeWhenSSE : activeWhenNoSSE
    return {
      activePollingConfig,
      backgroundPollingConfig,
      fetchOnceNoPollingConfig,
    }
  }

  const detailPollingConfig = isSSEConnected ? fetchOnceNoPollingConfig : detailWhenNoSSE
  return { detailPollingConfig }
}
